import { FC, useRef, useState } from 'react';
import { A11y, Navigation, Thumbs } from 'swiper/modules';
import SwiperCore from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';

import EVImg from '@components/EVImg/EVImg';
import { ChevronLeft, ChevronRight } from '@icon/Chevrons';
import { FloorPlan, Grid, StreetView, Video, VirtualTour } from '@icon/index';

//Nested Components
import GalleryFloorPlan from './GalleryFloorPlan';
import GalleryImages from './GalleryImages';
import GalleryModal from './GalleryModal';
import GalleryStreetView from './GalleryStreetView';
import GalleryVideo from './GalleryVideo';
import GalleryVirtualTour from './GalleryVirtualTour';
import { useStreetViewAvailability } from './useStreetViewAvailability';

import { generateListTracEvent } from '@util/ListTrac';
import { MediaGalleryData } from './mapData';
import {
  ImagesNavigation,
  ImagesNavigationNavButton,
  ImagesNavigationSwiper,
  MediaGalleryNavigation,
  MediaGalleryNavigationButton,
  MediaGalleryNavigationButtonSpan,
  Stage,
  StyledPropertyCooperativeAgencyStatus,
  StyledPropertyListingStatusWrapper,
} from './styles';

export interface MediaGalleryProps extends MediaGalleryData {}

const MediaGallery: FC<MediaGalleryData> = ({
  imagesArr,
  floorPlanArr,
  virtualTourData,
  streetViewData,
  videoData,
  listingStatus,
  cooperativeAgencyStatus,
  listingId,
  zipCode,
}) => {
  const [selectedButton, setSelectedButton] = useState('Images'); // state to store the selected button

  const handleMediaGalleryNavClick = async (buttonName) => {
    //if buttonName is same as selectedButton, then return
    if (buttonName === selectedButton) return;
    if (buttonName === 'VirtualTour') {
      await generateListTracEvent({ type: 'vTour', listingId, zipCode });
    }

    //if buttonName is "All Photos", then open modal
    if (buttonName === 'All Images') {
      //add "modal-open" class to the id "gallery-images-modal"
      document.getElementById('gallery-images-modal')?.classList.add('modal-open');
      //add "media-gallery-modal-open" to the body
      document.body.classList.add('media-gallery-modal-open');

      await generateListTracEvent({ type: 'gallery', listingId, zipCode });
    } else {
      //otherwise, switch the component
      setSelectedButton(buttonName);
    }
  };

  const renderSelectedOverlayComponent = () => {
    switch (selectedButton) {
      case 'FloorPlan':
        return (
          <GalleryFloorPlan floorPlanArr={floorPlanArr} setFloorPlanSwiper={setFloorPlanSwiper} />
        );
      case 'StreetView':
        return (
          <GalleryStreetView
            streetViewData={streetViewData!}
            isStreetViewAvailable={isStreetViewAvailable}
          />
        );
      case 'VirtualTour':
        return virtualTourData!.includes('yout') ? (
          <GalleryVideo videoData={virtualTourData!} />
        ) : (
          <GalleryVirtualTour virtualTourData={virtualTourData!} />
        );
      case 'Video':
        return <GalleryVideo videoData={videoData!} />;
      default:
        return null;
    }
  };

  const [imagesSwiper, setImagesSwiper] = useState<SwiperCore | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const [floorPlanSwiper, setFloorPlanSwiper] = useState<SwiperCore | null>(null);
  const [floorPlanModalSwiper, setFloorPlanModalSwiper] = useState<SwiperCore | null>(null);
  const isStreetViewAvailable = useStreetViewAvailability(streetViewData);

  // Images navigation buttons
  const thumbsPrevRef = useRef(null);
  const thumbsNextRef = useRef(null);

  return (
    <>
      <Stage>
        <GalleryImages
          imagesArr={imagesArr}
          thumbsSwiper={thumbsSwiper}
          setImagesSwiper={setImagesSwiper}
        />

        {renderSelectedOverlayComponent()}

        {listingStatus && (
          <StyledPropertyListingStatusWrapper>{listingStatus}</StyledPropertyListingStatusWrapper>
        )}
        {cooperativeAgencyStatus && (
          <StyledPropertyCooperativeAgencyStatus>
            {cooperativeAgencyStatus}
          </StyledPropertyCooperativeAgencyStatus>
        )}
      </Stage>

      <MediaGalleryNavigation>
        <ImagesNavigation onClick={() => handleMediaGalleryNavClick('Images')}>
          <ImagesNavigationNavButton
            type="button"
            className="images-navigation-prev mediagallerynavigation-button"
            data-testid="images-prev-nav-button"
            ref={thumbsPrevRef}
          >
            <ChevronLeft />
          </ImagesNavigationNavButton>
          <ImagesNavigationSwiper>
            <Swiper
              modules={[Navigation, A11y, Thumbs]}
              spaceBetween={0}
              slidesPerView={'auto'}
              slideToClickedSlide={true}
              navigation={{
                prevEl: thumbsPrevRef.current,
                nextEl: thumbsNextRef.current,
              }}
              watchSlidesProgress
              onSwiper={setThumbsSwiper}
            >
              {imagesArr &&
                imagesArr.length > 0 &&
                imagesArr.map((image, index) => {
                  return (
                    <SwiperSlide
                      key={index}
                      data-testid={`media-gallery-swiper-slide-${index + 1}`}
                    >
                      <EVImg
                        className="image-thumbs"
                        src={image}
                        alt="Listing Image Thumbnail"
                        sizes="84px"
                        data-testid={`media-gallery-img-${index + 1}`}
                      />
                    </SwiperSlide>
                  );
                })}
            </Swiper>
          </ImagesNavigationSwiper>
          <ImagesNavigationNavButton
            type="button"
            className="images-navigation-next mediagallerynavigation-button"
            data-testid="images-next-nav-button"
            ref={thumbsNextRef}
          >
            <ChevronRight />
          </ImagesNavigationNavButton>
        </ImagesNavigation>

        <MediaGalleryNavigationButton
          type="button"
          className="images-navigation-allphotos mediagallerynavigation-button"
          onClick={() => handleMediaGalleryNavClick('All Images')}
          data-testid="media-gallery-nav-button"
        >
          <Grid />
          <MediaGalleryNavigationButtonSpan>All Photos</MediaGalleryNavigationButtonSpan>
        </MediaGalleryNavigationButton>

        {floorPlanArr && floorPlanArr.length > 0 && (
          <MediaGalleryNavigationButton
            type="button"
            className="images-navigation-floorplan mediagallerynavigation-button"
            onClick={() => handleMediaGalleryNavClick('FloorPlan')}
            data-testid="floor-plan-nav-button"
          >
            <FloorPlan />
            <MediaGalleryNavigationButtonSpan>Floor Plan</MediaGalleryNavigationButtonSpan>
          </MediaGalleryNavigationButton>
        )}

        {virtualTourData && (
          <MediaGalleryNavigationButton
            type="button"
            className="images-navigation-virtualtour mediagallerynavigation-button"
            onClick={() => handleMediaGalleryNavClick('VirtualTour')}
            data-testid="virtual-tour-nav-button"
          >
            <VirtualTour />
            <MediaGalleryNavigationButtonSpan>Virtual Tour</MediaGalleryNavigationButtonSpan>
          </MediaGalleryNavigationButton>
        )}

        {streetViewData && isStreetViewAvailable && (
          <MediaGalleryNavigationButton
            type="button"
            className="images-navigation-streetview mediagallerynavigation-button"
            onClick={() => handleMediaGalleryNavClick('StreetView')}
            data-testid="street-view-nav-button"
          >
            <StreetView />
            <MediaGalleryNavigationButtonSpan>Street View</MediaGalleryNavigationButtonSpan>
          </MediaGalleryNavigationButton>
        )}

        {videoData && (
          <MediaGalleryNavigationButton
            type="button"
            className="images-navigation-video mediagallerynavigation-button"
            onClick={() => handleMediaGalleryNavClick('Video')}
            data-testid="video-nav-button"
          >
            <Video />
            <MediaGalleryNavigationButtonSpan>Video</MediaGalleryNavigationButtonSpan>
          </MediaGalleryNavigationButton>
        )}
      </MediaGalleryNavigation>

      <GalleryModal
        id="gallery-images-modal"
        imagesArr={imagesArr}
        floorPlanArr={floorPlanArr}
        setFloorPlanModalSwiper={setFloorPlanModalSwiper}
        virtualTourData={virtualTourData}
        streetViewData={streetViewData}
        isStreetViewAvailable={isStreetViewAvailable}
        videoData={videoData}
        setSelectedButton={setSelectedButton}
        listingId={listingId}
        zipCode={zipCode}
      />
    </>
  );
};

export default MediaGallery;
